import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaFileAlt, FaDownload, FaFilter, FaCalendarAlt,
  FaChartBar, FaChartLine, FaChartPie, FaFilePdf,
  FaFileExcel, FaUsers, FaTruck, FaBox, FaGasPump,
  FaWeightHanging, FaMapMarkerAlt, FaUser, FaClock
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function ReportsPage({ onLogout }) {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('tonnage-client');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filterClient, setFilterClient] = useState('');
  const [filterSubbie, setFilterSubbie] = useState('');
  const [filterCommodity, setFilterCommodity] = useState('');
  const [clients, setClients] = useState([]);
  const [subbies, setSubbies] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [truckSweatingData, setTruckSweatingData] = useState([]);

  // Turnover vs profit chart (financial report)
  const [chartMonths, setChartMonths] = useState(6);
  const [chartData, setChartData] = useState(null);
  const [salesData, setSalesData] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchClients();
    fetchSubbies();
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
    generateTruckSweatingReport();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      if (data.success) {
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchSubbies = async () => {
    try {
      const response = await fetch(`${API_URL}/subcontractors`);
      const data = await response.json();
      if (data.success) {
        setSubbies(data.subcontractors || []);
      }
    } catch (error) {
      console.error('Error fetching subbies:', error);
    }
  };

  // ============================================
  // GENERATE TRUCK SWEATING REPORT
  // ============================================
  const generateTruckSweatingReport = async () => {
    try {
      // Fetch jobs with requirements
      const jobsRes = await fetch(`${API_URL}/jobs`);
      const jobsData = await jobsRes.json();
      
      if (jobsData.success) {
        const jobs = jobsData.jobs || [];
        
        // Build truck sweating data from jobs
        const sweatingData = jobs.map(job => ({
          truck: job.truck_registration || 'N/A',
          driver: job.driver_name || 'N/A',
          loadNumber: job.job_number || 'N/A',
          route: `${job.origin || 'N/A'} → ${job.destination || 'N/A'}`,
          weightLoaded: job.loaded_volume_mt || 0,
          weightOffloaded: job.delivered_volume_mt || 0,
          fuelUsed: job.fuel_used || 0,
          status: job.status || 'pending',
          commodity: job.commodity || 'N/A',
          client: job.clients?.company_name || 'N/A',
          date: job.start_date || 'N/A'
        }));
        
        setTruckSweatingData(sweatingData);
      }
    } catch (error) {
      console.error('Error generating truck sweating report:', error);
    }
  };

  // ============================================
  // GENERATE REPORT
  // ============================================
  const generateReport = async () => {
    setLoading(true);
    try {
      let data = [];

      switch (reportType) {
        case 'tonnage-client':
          data = [
            { client: 'Chrome Mining Co.', tonnage: 4500, loads: 12, revenue: 2025000 },
            { client: 'Grain Traders SA', tonnage: 3200, loads: 8, revenue: 1216000 },
            { client: 'Export Logistics Ltd', tonnage: 2800, loads: 7, revenue: 1456000 }
          ];
          break;

        case 'tonnage-subbie':
          data = [
            { subcontractor: 'TPT Haulage', tonnage: 3800, loads: 10, revenue: 1710000 },
            { subcontractor: 'Cross-Border Express', tonnage: 2900, loads: 8, revenue: 1305000 },
            { subcontractor: 'Mighty Movers', tonnage: 2200, loads: 6, revenue: 990000 }
          ];
          break;

        case 'revenue':
          data = [
            { month: 'January', revenue: 450000, jobs: 3 },
            { month: 'February', revenue: 580000, jobs: 4 },
            { month: 'March', revenue: 720000, jobs: 5 }
          ];
          break;

        case 'truck-sweating':
          data = truckSweatingData.length > 0 ? truckSweatingData : [
            { truck: 'TRK-001', driver: 'John Doe', loadNumber: 'J-2026-001', route: 'Rustenburg → Durban', weightLoaded: 34.5, weightOffloaded: 34.2, fuelUsed: 450, status: 'completed' },
            { truck: 'TRK-002', driver: 'Jane Smith', loadNumber: 'J-2026-002', route: 'Johannesburg → Cape Town', weightLoaded: 28.0, weightOffloaded: 27.8, fuelUsed: 380, status: 'active' },
            { truck: 'TRK-003', driver: 'Bob Johnson', loadNumber: 'J-2026-003', route: 'Limpopo → Richards Bay', weightLoaded: 42.0, weightOffloaded: 41.5, fuelUsed: 520, status: 'completed' }
          ];
          break;

        case 'load-status':
          data = [
            { job: 'J-2026-001', client: 'Chrome Mining', commodity: 'Chrome', tons: 10000, status: 'Active' },
            { job: 'J-2026-002', client: 'Grain Traders', commodity: 'Maize', tons: 5000, status: 'Active' },
            { job: 'J-2026-003', client: 'Export Logistics', commodity: 'Manganese', tons: 8000, status: 'Completed' }
          ];
          break;

        case 'commodity':
          data = [
            { commodity: 'Chrome', totalTonnage: 15000, loads: 45, clients: 3 },
            { commodity: 'Maize', totalTonnage: 8000, loads: 20, clients: 2 },
            { commodity: 'Manganese', totalTonnage: 12000, loads: 35, clients: 4 }
          ];
          break;

        case 'driver-hours':
          data = [
            { driver: 'John Doe', hoursThisWeek: 22, hoursToday: 8, status: 'Compliant' },
            { driver: 'Jane Smith', hoursThisWeek: 24, hoursToday: 10, status: 'Exceeded' },
            { driver: 'Bob Johnson', hoursThisWeek: 19, hoursToday: 7, status: 'Compliant' }
          ];
          break;

        case 'turnover-profit': {
          const res = await fetch(`${API_URL}/accounts/turnover-profit?months=${chartMonths}`);
          const json = await res.json();
          if (json.success) {
            setChartData(json);
            data = (json.series || []).map((s) => ({
              Month: s.month,
              'Turnover (R)': s.turnover,
              'Profit (R)': s.profit,
              'Collected (R)': s.collected,
              'Invoices': s.invoices
            }));
          } else {
            data = [];
          }
          break;
        }

        case 'sales-by-salesperson': {
          const res = await fetch(`${API_URL}/accounts/sales-by-salesperson?months=${chartMonths}`);
          const json = await res.json();
          if (json.success) {
            setSalesData(json);
            data = (json.rows || []).map((r) => ({
              'Salesperson': r.name,
              'Revenue (R)': r.revenue,
              'Profit (R)': r.profit,
              'Collected (R)': r.collected,
              'Invoices': r.invoice_count,
              'Quotes': r.quotes_total,
              'Win Rate %': r.win_rate
            }));
          } else {
            data = [];
          }
          break;
        }

        default:
          data = [];
      }

      setReportData(data);
      toast.success('✅ Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EXPORT FUNCTIONALITY
  // ============================================
  const exportReport = (format) => {
    if (!reportData || reportData.length === 0) {
      toast.warning('No data to export. Generate a report first.');
      return;
    }

    const headers = Object.keys(reportData[0]);
    const filename = `${reportType}_${new Date().toISOString().split('T')[0]}`;

    if (format === 'csv') {
      // Export as CSV
      let csvContent = headers.join(',') + '\n';
      reportData.forEach(row => {
        const values = headers.map(header => {
          let value = row[header];
          if (typeof value === 'string' && value.includes(',')) {
            value = `"${value}"`;
          }
          return value;
        });
        csvContent += values.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`✅ Report exported as CSV!`);
    } else if (format === 'json') {
      // Export as JSON
      const jsonContent = JSON.stringify(reportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`✅ Report exported as JSON!`);
    } else {
      toast.info(`${format.toUpperCase()} export coming soon!`);
    }
  };

  // ============================================
  // REPORT TYPES
  // ============================================
  const reportTypes = [
    { value: 'tonnage-client', label: 'Tonnage by Client', icon: FaChartBar },
    { value: 'tonnage-subbie', label: 'Tonnage by Subcontractor', icon: FaChartBar },
    { value: 'jobs-overview', label: 'Jobs Overview', icon: FaChartPie },
    { value: 'revenue', label: 'Revenue Report', icon: FaChartLine },
    { value: 'load-status', label: 'Load Status Report', icon: FaBox },
    { value: 'commodity', label: 'Commodity Report', icon: FaTruck },
    { value: 'truck-sweating', label: '🚛 Truck Sweating Report', icon: FaTruck },
    { value: 'driver-hours', label: '🕐 Driver Hours Report', icon: FaClock },
    { value: 'turnover-profit', label: '💰 Turnover vs Profit', icon: FaChartLine },
    { value: 'sales-by-salesperson', label: '👤 Sales by Salesperson', icon: FaUser }
  ];

  const ReportIcon = reportTypes.find(r => r.value === reportType)?.icon || FaChartBar;

  return (
    <AdminLayout activeTab="reports" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📊 Reports</h1>
          <p className="text-gray-500 text-sm">Generate and export reports</p>
        </div>
      </div>

      {/* Report Controls */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            >
              {reportTypes.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subcontractor</label>
            <select
              value={filterSubbie}
              onChange={(e) => setFilterSubbie(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            >
              <option value="">All Subcontractors</option>
              {subbies.map(s => (
                <option key={s.id} value={s.id}>{s.company_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commodity</label>
            <input
              type="text"
              value={filterCommodity}
              onChange={(e) => setFilterCommodity(e.target.value)}
              placeholder="e.g. Chrome, Maize"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={generateReport}
              disabled={loading}
              className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>

          {reportType === 'turnover-profit' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Months</label>
              <select
                value={chartMonths}
                onChange={(e) => setChartMonths(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
          )}

          {reportType === 'sales-by-salesperson' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Months</label>
              <select
                value={chartMonths}
                onChange={(e) => setChartMonths(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="font-semibold text-navy">
              <ReportIcon className="inline mr-2" />
              {reportTypes.find(r => r.value === reportType)?.label}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => exportReport('csv')}
                className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100"
              >
                <FaFileExcel size={14} /> CSV
              </button>
              <button
                onClick={() => exportReport('json')}
                className="flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-600 rounded-lg text-sm hover:bg-purple-100"
              >
                <FaFileAlt size={14} /> JSON
              </button>
              <button
                onClick={() => exportReport('pdf')}
                className="flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100"
              >
                <FaFilePdf size={14} /> PDF
              </button>
            </div>
          </div>
          <div className="p-6">
            {reportType === 'sales-by-salesperson' && salesData && salesData.rows && salesData.rows.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg px-4 py-2">
                    <div className="text-xs text-blue-600">Total Revenue ({salesData.months} mo)</div>
                    <div className="text-lg font-bold text-navy">R {salesData.rows.reduce((a, r) => a + (r.revenue || 0), 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg px-4 py-2">
                    <div className="text-xs text-purple-600">Total Profit</div>
                    <div className="text-lg font-bold text-navy">R {salesData.rows.reduce((a, r) => a + (r.profit || 0), 0).toLocaleString()}</div>
                  </div>
                </div>
                <div className="flex items-end gap-3" style={{ height: '200px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                  {salesData.rows.map((r) => {
                    const max = Math.max(...salesData.rows.map((x) => x.revenue || 0), 1);
                    const h = Math.max(((r.revenue || 0) / max) * 160, 2);
                    return (
                      <div key={r.salesperson_id || 'un' + r.name} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <div className="text-[10px] text-gray-500 font-medium">R{(r.revenue || 0).toLocaleString()}</div>
                        <div className="w-full rounded-t" style={{ height: `${h}px`, backgroundColor: '#3B82F6' }} title={`${r.name}: R${(r.revenue || 0).toLocaleString()} · Win rate ${r.win_rate}%`}></div>
                        <div className="text-[10px] text-gray-400 truncate w-full text-center">{r.name}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#3B82F6' }}></span> Revenue per salesperson</span>
                </div>
              </div>
            )}
            {reportType === 'turnover-profit' && chartData && chartData.series && chartData.series.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg px-4 py-2">
                    <div className="text-xs text-blue-600">Total Turnover</div>
                    <div className="text-lg font-bold text-navy">R {(chartData.totals?.turnover || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg px-4 py-2">
                    <div className="text-xs text-purple-600">Total Gross Profit</div>
                    <div className="text-lg font-bold text-navy">R {(chartData.totals?.profit || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg px-4 py-2">
                    <div className="text-xs text-green-600">Total Collected</div>
                    <div className="text-lg font-bold text-navy">R {(chartData.totals?.collected || 0).toLocaleString()}</div>
                  </div>
                </div>

                <div className="flex items-end gap-3" style={{ height: '220px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                  {chartData.series.map((s) => {
                    const max = Math.max(...chartData.series.map((x) => x.turnover || 0), 1);
                    const turnoverH = Math.max(((s.turnover || 0) / max) * 180, 2);
                    const profitH = Math.max(((s.profit || 0) / max) * 180, s.profit > 0 ? 2 : 0);
                    return (
                      <div key={s.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <div className="text-[10px] text-gray-500 font-medium">R{(s.turnover || 0).toLocaleString()}</div>
                        <div className="w-full flex items-end justify-center gap-1">
                          <div
                            className="rounded-t"
                            style={{ width: '38%', height: `${turnoverH}px`, backgroundColor: '#3B82F6', minHeight: s.turnover > 0 ? '2px' : 0 }}
                            title={`Turnover ${s.month}: R${(s.turnover || 0).toLocaleString()}`}
                          ></div>
                          <div
                            className="rounded-t"
                            style={{ width: '38%', height: `${profitH}px`, backgroundColor: '#8B5CF6', minHeight: s.profit > 0 ? '2px' : 0 }}
                            title={`Profit ${s.month}: R${(s.profit || 0).toLocaleString()}`}
                          ></div>
                        </div>
                        <div className="text-[10px] text-gray-400">{s.month.slice(0, 7)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#3B82F6' }}></span> Turnover</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#8B5CF6' }}></span> Gross Profit</span>
                </div>
              </div>
            )}
            {reportData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No data found for the selected filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      {Object.keys(reportData[0] || {}).map((key) => (
                        <th key={key} className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        {Object.values(row).map((value, i) => (
                          <td key={i} className="px-4 py-2 text-sm text-gray-600">
                            {typeof value === 'number' ? value.toLocaleString() : value || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ReportsPage;
